const BLOCKS_PER_HALVING = 210_000;
const SATOSHIS_PER_BTC = 100_000_000;
const INITIAL_BLOCK_REWARD_SATS = 50 * SATOSHIS_PER_BTC;
const DEFAULT_PROJECTED_EPOCHS_AHEAD = 10;
const MINUTES_PER_DAY = 24 * 60;
const DAYS_PER_YEAR = 365.25;

const GENESIS_DATE = new Date("2009-01-03T00:00:00Z");

const KNOWN_HALVING_DATES: Record<number, string> = {
  210_000: "2012-11-28T00:00:00Z",
  420_000: "2016-07-09T00:00:00Z",
  630_000: "2020-05-11T00:00:00Z",
  840_000: "2024-04-20T00:00:00Z",
};

export type HalvingEraStatus = "Completed" | "Current" | "Upcoming";
export type HalvingDateBasis = "Actual" | "Estimated";

export type HalvingEraRow = {
  epoch: number;
  startBlock: number;
  halvingBlock: number;
  rewardBtcPerBlock: number;
  btcIssuedInEpoch: number;
  cumulativeSupplyBtc: number;
  startDate: Date;
  halvingDate: Date;
  startDateBasis: HalvingDateBasis;
  halvingDateBasis: HalvingDateBasis;
  status: HalvingEraStatus;
  blocksUntilHalving: number;
  estimatedYearsUntilHalving: number;
};

export type HalvingSchedule = {
  rows: HalvingEraRow[];
  currentEpoch: number;
  averageBlockMinutes: number;
  estimatedCycleYears: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getRewardInSats = (epoch: number) =>
  Math.floor(INITIAL_BLOCK_REWARD_SATS / Math.pow(2, epoch - 1));

const getProjectedEpochsAhead = (currentEpoch: number, value?: number) => {
  const projected = value ?? DEFAULT_PROJECTED_EPOCHS_AHEAD;
  return Math.max(4, projected + Math.max(currentEpoch - 1, 0));
};

const getDateForHalvingBlock = (
  halvingBlock: number,
  currentBlock: number,
  now: Date,
  averageBlockMinutes: number,
): { date: Date; basis: HalvingDateBasis } => {
  const knownDateIso = KNOWN_HALVING_DATES[halvingBlock];
  if (knownDateIso) {
    return { date: new Date(knownDateIso), basis: "Actual" };
  }

  const blocksDelta = halvingBlock - currentBlock;
  const minutesDelta = blocksDelta * averageBlockMinutes;
  return {
    date: new Date(now.getTime() + minutesDelta * 60 * 1000),
    basis: "Estimated",
  };
};

export const buildHalvingSchedule = ({
  currentBlock,
  now = new Date(),
  projectedEpochsAhead,
}: {
  currentBlock: number;
  now?: Date;
  projectedEpochsAhead?: number;
}): HalvingSchedule => {
  const safeCurrentBlock = Number.isFinite(currentBlock)
    ? Math.max(Math.floor(currentBlock), 0)
    : 0;

  const currentEpoch = Math.floor(safeCurrentBlock / BLOCKS_PER_HALVING) + 1;
  const averageBlockMinutes = clamp(
    (now.getTime() - GENESIS_DATE.getTime()) / (safeCurrentBlock || 1) / 60_000,
    8,
    14,
  );

  const lastEpoch = getProjectedEpochsAhead(currentEpoch, projectedEpochsAhead);
  const rows: HalvingEraRow[] = [];
  let cumulativeSupply = 0;

  for (let epoch = 1; epoch <= lastEpoch; epoch += 1) {
    const startBlock = (epoch - 1) * BLOCKS_PER_HALVING;
    const halvingBlock = epoch * BLOCKS_PER_HALVING;
    const rewardSats = getRewardInSats(epoch);
    const rewardBtcPerBlock = rewardSats / SATOSHIS_PER_BTC;
    const btcIssuedInEpoch = rewardBtcPerBlock * BLOCKS_PER_HALVING;
    cumulativeSupply += btcIssuedInEpoch;

    const halvingDateInfo = getDateForHalvingBlock(
      halvingBlock,
      safeCurrentBlock,
      now,
      averageBlockMinutes,
    );

    const previousRow = rows[rows.length - 1];
    const startDate = previousRow ? previousRow.halvingDate : GENESIS_DATE;
    const startDateBasis = previousRow ? previousRow.halvingDateBasis : "Actual";

    const status: HalvingEraStatus =
      safeCurrentBlock >= halvingBlock
        ? "Completed"
        : safeCurrentBlock >= startBlock
          ? "Current"
          : "Upcoming";

    const blocksUntilHalving = Math.max(halvingBlock - safeCurrentBlock, 0);
    const estimatedYearsUntilHalving =
      (blocksUntilHalving * averageBlockMinutes) / MINUTES_PER_DAY / DAYS_PER_YEAR;

    rows.push({
      epoch,
      startBlock,
      halvingBlock,
      rewardBtcPerBlock,
      btcIssuedInEpoch,
      cumulativeSupplyBtc: cumulativeSupply,
      startDate,
      halvingDate: halvingDateInfo.date,
      startDateBasis,
      halvingDateBasis: halvingDateInfo.basis,
      status,
      blocksUntilHalving,
      estimatedYearsUntilHalving,
    });

    if (rewardSats <= 1 && status !== "Completed") {
      break;
    }
  }

  return {
    rows,
    currentEpoch,
    averageBlockMinutes,
    estimatedCycleYears:
      (BLOCKS_PER_HALVING * averageBlockMinutes) / MINUTES_PER_DAY / DAYS_PER_YEAR,
  };
};

